import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, catchError, map, of, throwError } from 'rxjs';
import { z } from 'zod';

const AffectedWorkspaceCountResponseSchema = z.object({
  count: z.number().int().nonnegative(),
});

/**
 * How the current `rawText`/markdown statement was produced. Mirrors the backend
 * `OrgIntentSourceKind` enum (`OrgSecurityIntentEntity.kt`) verbatim.
 */
export type OrgIntentSourceKind = 'TEXT_INPUT' | 'MD_UPLOAD' | 'AI_GENERATED';

/**
 * Read shape for a single org security-intent version. Mirrors
 * `OrgSecurityIntentController.OrgIntentView` (T5) field-for-field. The structured
 * fields (`declaredCriticality`, `declaredRegulatoryScope`, `dataClasses`, `threatFocus`,
 * `injectionSuspected`) are carried through so a version-history row can be rendered
 * faithfully, but this plan's dialog does not expose them as editable fields — text
 * fields only, per the plan's stated scope.
 */
export interface OrgIntentView {
  id: string;
  organizationId: string;
  orgDescription: string | null;
  orgContext: string | null;
  orgSecurityContext: string | null;
  contextIncluded: boolean;
  securityContextIncluded: boolean;
  rawText: string | null;
  sourceKind: OrgIntentSourceKind;
  declaredCriticality: string;
  declaredRegulatoryScope: string[];
  dataClasses: string[];
  threatFocus: string[];
  injectionSuspected: boolean;
  version: number;
  setByUserId: string;
  setAt: string;
}

/** Write shape for `PUT /api/intel/org-intent`. Mirrors `OrgIntentUpsertRequest` (T5) verbatim. */
export interface OrgIntentUpsertRequest {
  orgDescription?: string | null;
  orgContext?: string | null;
  orgSecurityContext?: string | null;
  contextIncluded: boolean;
  securityContextIncluded: boolean;
  rawText?: string | null;
  sourceKind?: OrgIntentSourceKind;
}

/**
 * Preview-only response for `POST /api/intel/org-intent/enhance`. Mirrors
 * `EnhancePreviewResponse` (T5) verbatim — the backend performs NO write for this call, it only
 * runs the submitted text through `OrgIntentIngestGuard.inspect` and reports the verdict.
 */
export interface EnhancePreviewResponse {
  rawText: string;
  injectionSuspected: boolean;
  taintedFields: string[];
  truncated: boolean;
}

/**
 * Angular client for the org-grain security intent REST surface (`OrgSecurityIntentController`,
 * T5). Every route is org-admin-gated server-side and resolves the caller's OWN organization from
 * request context — there is no id-addressable cross-org resource on this surface, so this client
 * never accepts an `orgId`/`organizationId` parameter (`.claude/rules/tenant-isolation.md`: org
 * comes from context, never a client-supplied value).
 *
 * Endpoints:
 *   GET  /api/intel/org-intent                     — current LIVE statement, or `null` on 404 (none set yet).
 *   PUT  /api/intel/org-intent                     — insert-only versioned upsert.
 *   GET  /api/intel/org-intent/history             — full version history, newest first.
 *   POST /api/intel/org-intent/enhance              — preview-only ingest-guard inspection; writes nothing.
 *   GET  /api/intel/org-intent/affected-workspace-count — how many workspaces the org has right now
 *                                                     (Task 6's `OrgIntentFanoutService`, rendered by
 *                                                     the dialog before a save). Meaningful even
 *                                                     before the org's first save — does not depend
 *                                                     on a live org-intent row existing.
 */
@Injectable({ providedIn: 'root' })
export class OrgIntentService {
  private readonly http = inject(HttpClient);
  private readonly base = '/api/intel/org-intent';

  /**
   * The org's current statement, or `null` when none has ever been set (backend 404) —
   * distinguished from a genuine transport/server error, which is rethrown for the caller to
   * surface via the toast pipeline.
   */
  get(): Observable<OrgIntentView | null> {
    return this.http.get<OrgIntentView>(this.base).pipe(
      catchError((err: HttpErrorResponse) => (err.status === 404 ? of(null) : throwError(() => err))),
    );
  }

  save(payload: OrgIntentUpsertRequest): Observable<OrgIntentView> {
    return this.http.put<OrgIntentView>(this.base, payload);
  }

  history(): Observable<OrgIntentView[]> {
    return this.http.get<OrgIntentView[]>(`${this.base}/history`);
  }

  /** Preview only — never applies the result. The caller decides whether to accept it. */
  enhance(rawText: string): Observable<EnhancePreviewResponse> {
    return this.http.post<EnhancePreviewResponse>(`${this.base}/enhance`, { rawText });
  }

  /**
   * How many workspaces the org has right now — rendered by the dialog before a save so an admin
   * sees how many workspace threat models an org security-context edit will mark stale.
   */
  affectedWorkspaceCount(): Observable<number> {
    return this.http
      .get<unknown>(`${this.base}/affected-workspace-count`)
      .pipe(map((body) => AffectedWorkspaceCountResponseSchema.parse(body).count));
  }
}
